import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { IMAGES } from "@/data";

const shots = [
  {
    src: IMAGES.heroCheering,
    alt: "Campers cheering with hands raised around the Action Packed Adventures logo",
    caption: "Big smiles all round",
  },
  {
    src: IMAGES.coachesLogo,
    alt: "Two campers proudly showing off their colourful artwork",
    caption: "Arts & crafts",
  },
  {
    src: IMAGES.coachChild,
    alt: "Two campers enjoying the woodland play area",
    caption: "Time to explore",
  },
  {
    src: IMAGES.gallerySupplement,
    alt: "Heading out to the playing field with a coach on a camp day",
    caption: "Out to the field",
  },
  {
    src: IMAGES.galleryFence,
    alt: "A camper meeting the animals on a day trip",
    caption: "Day-trip adventures",
  },
  {
    src: IMAGES.galleryTable,
    alt: "Two campers grinning at snack time",
    caption: "Snack-time fun",
  },
  {
    src: "/images/IMG_6316.webp",
    alt: "Two campers on the slide at the wooden play fort",
    caption: "Playground pals",
  },
  {
    src: "/images/IMG_6214.webp",
    alt: "Campers absorbed in their artwork in the art room",
    caption: "Getting creative",
  },
  {
    src: "/images/IMG_6182.webp",
    alt: "A camper striking a football on the all-weather pitch",
    caption: "On the pitch",
  },
  {
    src: "/images/IMG_6230.webp",
    alt: "Campers and coaches making colourful decorated crowns",
    caption: "Crown-making crew",
  },
];

const variants = {
  enter: (dir) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0 }),
};

export const Gallery = () => {
  const [[index, dir], setState] = useState([0, 0]);
  const [paused, setPaused] = useState(false);

  const paginate = useCallback((d) => {
    setState(([i]) => [(i + d + shots.length) % shots.length, d]);
  }, []);

  const goTo = (i) => setState(([cur]) => [i, i > cur ? 1 : -1]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => paginate(1), 5000);
    return () => clearInterval(id);
  }, [paused, paginate, index]);

  const current = shots[index];

  return (
    <section
      id="gallery"
      className="relative bg-slate-50 py-24 md:py-36"
      data-testid="gallery-section"
    >
      <div className="mx-auto max-w-6xl px-5">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <span className="inline-block font-display font-bold text-sky-500 uppercase tracking-widest text-sm mb-4">
              The Gallery
            </span>
            <h2 className="font-display font-black tracking-tight text-slate-900 text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
              Smiles that say <span className="text-sky-500">it all.</span>
            </h2>
          </div>
          <p className="text-lg text-slate-600 font-medium max-w-sm">
            Click through a few moments from our camps — happy faces, good
            friends and plenty to get involved in.
          </p>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          data-testid="gallery-carousel"
        >
          <div className="relative overflow-hidden rounded-[2.5rem] border-2 border-white shadow-[0_30px_70px_rgba(15,23,42,0.15)] bg-slate-200 aspect-[16/10]">
            <AnimatePresence initial={false} custom={dir} mode="popLayout">
              <motion.img
                key={index}
                src={current.src}
                alt={current.alt}
                custom={dir}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 260, damping: 32 },
                  opacity: { duration: 0.3 },
                }}
                data-testid="gallery-image"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </AnimatePresence>

            {/* caption */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/70 to-transparent p-6 md:p-8 pointer-events-none">
              <p className="font-display font-extrabold text-white text-xl md:text-2xl">
                {current.caption}
              </p>
            </div>

            {/* counter */}
            <div
              className="absolute top-5 right-5 rounded-full bg-slate-900/60 backdrop-blur px-3 py-1 text-sm font-bold text-white"
              data-testid="gallery-counter"
            >
              {index + 1} / {shots.length}
            </div>
          </div>

          {/* arrows */}
          <button
            onClick={() => paginate(-1)}
            data-testid="gallery-prev"
            aria-label="Previous photo"
            className="absolute left-3 md:-left-5 top-1/2 -translate-y-1/2 grid h-12 w-12 place-items-center rounded-full bg-white text-slate-900 shadow-lg hover:bg-sky-400 hover:text-white transition-colors"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={() => paginate(1)}
            data-testid="gallery-next"
            aria-label="Next photo"
            className="absolute right-3 md:-right-5 top-1/2 -translate-y-1/2 grid h-12 w-12 place-items-center rounded-full bg-white text-slate-900 shadow-lg hover:bg-sky-400 hover:text-white transition-colors"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          {/* dots */}
          <div className="mt-6 flex items-center justify-center gap-2.5">
            {shots.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                data-testid={`gallery-dot-${i}`}
                aria-label={`Go to photo ${i + 1}`}
                className={`h-2.5 rounded-full transition-all ${
                  i === index
                    ? "w-8 bg-sky-500"
                    : "w-2.5 bg-slate-300 hover:bg-slate-400"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Gallery;
